<template>
  <v-dialog :value="value" max-width="520" persistent scrollable @input="$emit('input', $event)">
    <!-- Vuetify's scrollable CSS only reaches `> .v-card` and `> form > .v-card`, so moving the
         observer back inside the card unpins the title and the actions. `@submit.prevent` drops
         the implicit submission Enter triggers now that this is a real form. -->
    <validation-observer ref="form" v-slot="observer" tag="form" @submit.prevent>
      <v-card>
        <v-card-title class="justify-center document-field-title">
          {{ t(formValue ? 'editField' : 'newField') }}
        </v-card-title>
        <v-card-text>
          <validation-provider v-slot="{ errors }" :name="t('label')" rules="label_name">
            <v-text-field
              v-model="localeLabel"
              :label="t('label') + (locale === 'en' ? ' *' : '')"
              :aria-required="locale === 'en'"
              :placeholder="t('labelExample')"
              :hint="t('labelHint')"
              persistent-hint
              outlined
              dense
              :error-messages="errors[0] || error(locale)"
              class="mt-2"
              @input="clearError(locale)"
            />
          </validation-provider>
          <v-tabs v-model="activeTab" class="mt-4">
            <v-tab>{{ t('general') }}</v-tab>
            <v-tab>{{ t('translations') }}</v-tab>
          </v-tabs>
          <v-tabs-items v-model="activeTab">
            <v-tab-item eager>
              <v-select
                v-model="draft.document_types"
                :items="documentTypes"
                :label="t('documentType')"
                :placeholder="t('select')"
                persistent-placeholder
                multiple
                chips
                small-chips
                clearable
                :hint="t('allTypesHint')"
                persistent-hint
                :error-messages="error('document_types')"
                outlined
                dense
                class="mt-5"
              />
              <validation-provider v-slot="{ errors }" :name="t('key')" rules="required|document_field_key">
                <v-text-field
                  v-model="draft.key"
                  :label="t('key') + ' *'"
                  aria-required="true"
                  :placeholder="t('keyExample')"
                  :hint="t('keyHint')"
                  persistent-hint
                  :disabled="!!formValue"
                  :error-messages="errors[0] || error('key')"
                  outlined
                  dense
                  @input="clearError('key')"
                />
              </validation-provider>
              <validation-provider v-slot="{ errors }" :name="t('dataType')" rules="required">
                <v-select
                  v-model="draft.data_type"
                  :items="dataTypes"
                  :label="t('dataType') + ' *'"
                  aria-required="true"
                  :placeholder="t('select')"
                  persistent-placeholder
                  :error-messages="errors[0] || error('data_type')"
                  outlined
                  dense
                  class="mt-4"
                  @change="clearError('data_type')"
                />
              </validation-provider>
              <v-textarea v-model="draft.description" :label="t('description')" outlined dense rows="2" />
            </v-tab-item>
            <v-tab-item eager>
              <div v-for="language in languages" :key="language.code" class="d-flex align-center mt-4">
                <div class="lang-label mr-3">
                  <div class="flag-icon mr-2" :class="'flag-icon-' + language.flag"></div>
                  <span class="text-uppercase">{{ language.code }}</span>
                </div>
                <validation-provider v-slot="{ errors }" :name="language.name" rules="label_name" class="flex-grow-1">
                  <v-text-field
                    v-model="draft.labels[language.code]"
                    :label="language.name + (language.code === 'en' ? ' *' : '')"
                    :aria-required="language.code === 'en'"
                    :error-messages="errors[0] || error(language.code)"
                    outlined
                    dense
                    hide-details="auto"
                    @input="clearError(language.code)"
                  />
                </validation-provider>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
        <v-card-actions class="pa-0">
          <!-- The english label and the key uniqueness are checked on click rather than by the observer:
               both sit outside the tab being looked at, so greying the button on them would leave the
               user with no way to reach what is wrong. Everything the observer does grey is a field
               whose message is already displayed underneath it. -->
          <v-btn color="primary" tile width="50%" :disabled="observer.invalid" @click="validate">
            {{ t('validate') }}
          </v-btn>
          <v-btn color="secondary" class="ml-0" tile width="50%" @click="cancel">{{ t('cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </validation-observer>
  </v-dialog>
</template>

<script>
  import { ValidationObserver, ValidationProvider } from 'vee-validate'
  import { SUPPORTED_LANGUAGES } from '@/constants/languages'
  import {
    DOCUMENT_FIELD_TYPES,
    DOCUMENT_DATA_TYPES,
    newDocumentField,
    documentFieldErrors,
    toDocumentFieldPayload,
  } from '@/helpers/documentCustomFields'

  export default {
    name: 'DocumentCustomFieldDialog',
    components: { ValidationObserver, ValidationProvider },
    props: {
      value: { type: Boolean, default: false },
      formValue: { type: Object, default: null },
      fields: { type: Array, required: true },
    },
    data() {
      const draft = this.formValue ? JSON.parse(JSON.stringify(this.formValue)) : newDocumentField()
      draft.labels = Object.fromEntries(SUPPORTED_LANGUAGES.map(({ code }) => [code, draft.labels?.[code] || '']))
      // The payload drops a blank description, so an edited field may arrive without the key at all.
      // It has to exist before data() returns, or the textarea would write a non reactive property.
      draft.description = draft.description || ''
      return { draft, activeTab: 0, errors: {}, languages: SUPPORTED_LANGUAGES }
    },
    computed: {
      locale() {
        return SUPPORTED_LANGUAGES.some(({ code }) => code === this.$i18n.locale) ? this.$i18n.locale : 'en'
      },
      localeLabel: {
        get() {
          return this.draft.labels[this.locale]
        },
        set(value) {
          this.$set(this.draft.labels, this.locale, value)
        },
      },
      documentTypes() {
        return DOCUMENT_FIELD_TYPES.map((value) => ({ value, text: this.t(`types.${value}`) }))
      },
      dataTypes() {
        return DOCUMENT_DATA_TYPES.map((value) => ({ value, text: this.t(`dataTypes.${value}`) }))
      },
    },
    methods: {
      t(key) {
        return this.$t(`documentSettings.${key}`)
      },
      error(key) {
        return this.errors[key] ? this.t(this.errors[key]) : ''
      },
      clearError(key) {
        if (this.errors[key]) {
          this.$delete(this.errors, key)
        }
      },
      cancel() {
        this.$emit('input', false)
      },
      async validate() {
        this.errors = documentFieldErrors(this.draft, this.fields, this.formValue ? this.formValue.key : null)
        // Runs even when the helper is happy: it is what puts the observer messages on screen.
        const valid = await this.$refs.form.validate()
        if (Object.keys(this.errors).length || !valid) {
          this.activeTab = this.languages.some(({ code }) => this.errors[code]) ? 1 : 0
          return
        }
        this.$emit('validate', toDocumentFieldPayload(this.draft))
        this.$emit('input', false)
      },
    },
  }
</script>

<style scoped lang="scss">
  @import '@/assets/styles/flags.scss';

  .document-field-title {
    word-break: normal;
  }

  .lang-label {
    min-width: 70px;
    display: flex;
    align-items: center;
  }
</style>
